import React, { useMemo, useState } from "react";

import styled from "styled-components";

import {
  Dropdown,
  type DropdownItemProps,
  type SemanticICONS,
} from "simplydo/react/ui";

import util from "utils/utils";

export const StyledSelectableDropdown = styled(Dropdown)<{
  $icon?: SemanticICONS;
}>`
  a.ui.label {
    display: none !important;
  }

  > .divider.text,
  .search {
    margin-top: 0.512381em !important;
  }

  ${(props) =>
    props.$icon &&
    `
    >.divider.text, .search {
      margin-left: 2.67142857em !important;
    }
    .ui.icon.input.left {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
    }
    input:focus~.ui.icon.input.left .icon {
      opacity: 1 !important;
    }
  `}
`;

type SelectableItemProps = {
  option: DropdownItemProps;
  single: boolean;
  isSelected: boolean;
  isHighlighted: boolean;
  onSelect: () => void;
  setHighlight: (isHighlighted: boolean) => void;
};

const StyledSelectableItem = styled(Dropdown.Item)`
  img {
    width: 20px !important;
    height: 20px !important;
  }
`;

const SelectableItem = ({
  option,
  single,
  isSelected,
  onSelect,
  isHighlighted,
  setHighlight,
}: SelectableItemProps) => (
  <StyledSelectableItem
    style={{
      display: "flex",
      alignItems: "center",
      background: isHighlighted ? "rgba(0,0,0,.05)" : null,
    }}
    key={option.key}
    value={option.value}
    text={
      option.description ?
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span
            style={{ fontWeight: isSelected && single ? "bold" : "normal" }}
          >
            {option.text}
          </span>
          <span style={{ fontSize: "0.9em", color: "#999", marginTop: 2 }}>
            {option.description as string}
          </span>
        </div>
      : <span style={{ fontWeight: isSelected && single ? "bold" : "normal" }}>
          {option.text}
        </span>
    }
    image={option.image}
    icon={isSelected && !single ? "checkmark" : undefined}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onSelect();
    }}
    onMouseEnter={() => setHighlight(true)}
    onMouseLeave={() => setHighlight(false)}
  />
);

export type SelectableDropdownOptions = (DropdownItemProps & { key: string })[];

type ISelectableDropdown = {
  placeholder?: string;
  pluralName?: string;
  single?: boolean;
  fluid?: boolean;
  icon?: SemanticICONS;
  searchable?: boolean;
  className?: string;
  options: SelectableDropdownOptions;
  style?: React.CSSProperties;
  clearable?: boolean;
  searchQuery?: string;
} & (
  | {
      value: string[];
      single?: false | undefined;
      searchIsOption?: false; // searchIsOptions is currently only available for single selections
      onChange: (
        changedValue: string[],
        empty: null,
        option: DropdownItemProps | null,
      ) => void;
    }
  | {
      value: string;
      single: true;
      searchIsOption?: boolean;
      onChange: (
        changedValue: string | null,
        optionKey: string | null,
        option: DropdownItemProps | null,
      ) => void;
    }
);

const SelectableDropdown = ({
  value,
  placeholder,
  pluralName = "options",
  onChange,
  options,
  fluid = true,
  searchable = false,
  searchIsOption = false,
  single,
  style,
  icon,
  clearable = true,
  className,
}: ISelectableDropdown) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightIndex, setHighlightIndex] = useState<null | number>(null);

  const normalisedSelections = single === true ? [value] : value;
  const selections = !value ? [] : normalisedSelections;
  let availableOptions = options;

  if (searchIsOption && single) {
    if (value && !availableOptions.find((option) => option.value === value)) {
      availableOptions = [
        { key: value, value, text: value },
        ...availableOptions,
      ];
    }
    if (
      searchTerm &&
      (!value || (value && searchTerm.toLowerCase() !== value.toLowerCase()))
    ) {
      availableOptions = [
        {
          key: searchTerm,
          value: searchTerm,
          text: `${searchTerm} - Use custom search term`,
        },
        ...availableOptions,
      ];
    }
  }

  const visibleOptions = useMemo(
    () =>
      searchable ?
        availableOptions.filter(
          (option) =>
            `${option.value}`
              .toLowerCase()
              .indexOf(searchTerm.toLowerCase()) !== -1 ||
            `${option.text}`.toLowerCase().indexOf(searchTerm.toLowerCase()) !==
              -1,
        )
      : availableOptions,
    [searchable, availableOptions, searchTerm],
  );

  let formattedText: React.ReactNode | undefined;
  if (selections) {
    if (selections.length === 1) {
      // Display the tag value directly
      formattedText =
        availableOptions.find((o) => o.value === selections[0])?.text ||
        undefined;
    } else if (selections.length > 1) {
      // Display pluralised text, single option not possible
      formattedText = util.pluralise(
        selections.length,
        "",
        `${pluralName} selected`,
        true,
      );
    }
  }

  const selectOption = (isSelected: boolean, option: DropdownItemProps) => {
    if (single === true) {
      onChange(
        isSelected ? null : `${option.value}`,
        isSelected ? null : `${option.key ?? option.value}`,
        option,
      );
    } else if (isSelected) {
      onChange(
        selections.filter((v) => v !== option.value),
        null,
        option,
      );
    } else {
      onChange([...selections, `${option.value}`], null, option);
    }
    if (searchable) {
      setSearchTerm("");
    }
    setOpen(false);
  };

  return (
    <StyledSelectableDropdown
      disabled={options?.length === 0}
      className={`selection ${className || ""}`}
      $icon={icon}
      icon={
        <>
          <i aria-hidden="true" className="dropdown icon"></i>
          {icon ?
            <div className="ui input left icon">
              <i aria-hidden="true" className={`${icon} icon`}></i>
            </div>
          : null}
        </>
      }
      multiple
      open={open}
      value={selections}
      search={searchable}
      searchInput={{
        autoComplete: "off",
        name: "search-filter",
        "data-bwignore": true,
        "data-1p-ignore": true,
        "data-lpignore": "true",
        "data-form-type": "other",
      }}
      searchQuery={searchable ? searchTerm : undefined}
      onSearchChange={(_e, { searchQuery }) => {
        setSearchTerm(searchQuery);
        setOpen(true);
      }}
      onClick={() => setOpen(true)}
      fluid={fluid}
      onClose={() => setOpen(false)}
      clearable={clearable}
      {...(typeof formattedText === "string" ? { text: formattedText }
      : typeof formattedText === "number" ? { text: `${formattedText}` }
      : {})}
      placeholder={placeholder}
      style={style}
      onChange={(_e, { value: changedValue }) => {
        if (!Array.isArray(changedValue) || changedValue.length !== 0) {
          return;
        }
        if (single === true) {
          onChange(null, null, null);
          return;
        }
        onChange([], null, null);
      }}
      onBlur={() => {
        setHighlightIndex(null);
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        const { code } = e;
        if (code === "ArrowDown") {
          setHighlightIndex(
            highlightIndex !== null ?
              (highlightIndex + 1) % availableOptions.length
            : 0,
          );
        }
        if (code === "ArrowUp") {
          let newIndex = (highlightIndex ?? 0) - 1;
          if (newIndex < 0) {
            newIndex = availableOptions.length - 1;
          }
          setHighlightIndex(
            highlightIndex !== null ? newIndex : availableOptions.length - 1,
          );
        }
        if (code === "Enter") {
          const firstOption = availableOptions[0];
          if (availableOptions.length === 1 && firstOption) {
            return selectOption(
              selections.indexOf(`${firstOption.value}`) > -1,
              firstOption,
            );
          }
          if (highlightIndex === null) return;
          const highlightedOption = availableOptions[highlightIndex];
          if (!highlightedOption) return;
          const isSelected =
            selections.indexOf(`${highlightedOption.value}`) > -1;
          selectOption(isSelected, highlightedOption);
        }
      }}
    >
      <Dropdown.Menu>
        {visibleOptions.map((option, i) => {
          const isSelected = selections.indexOf(`${option.value}`) > -1;
          if (option?.isDivider)
            return (
              <React.Fragment key={option.key}>
                <Dropdown.Divider style={{ margin: 0 }} />
                {option.text ?
                  <Dropdown.Header style={{ margin: "5px 0" }}>
                    {option.text}
                  </Dropdown.Header>
                : null}
              </React.Fragment>
            );
          return (
            <SelectableItem
              key={option.key}
              option={option}
              single={single === true}
              isSelected={isSelected}
              isHighlighted={highlightIndex === i}
              setHighlight={(isHighlighted) =>
                isHighlighted ? setHighlightIndex(i) : setHighlightIndex(null)
              }
              onSelect={() => selectOption(isSelected, option)}
            />
          );
        })}
      </Dropdown.Menu>
    </StyledSelectableDropdown>
  );
};

export default SelectableDropdown;
